/**
 * W-3 · Utilisateurs.
 *
 * Écart assumé au contrat : `API.md` n'expose qu'une métrique d'uniques
 * (`active_users`). WAU et MAU ne peuvent donc PAS être des séries — et surtout
 * on ne peut pas les fabriquer en sommant des DAU, parce qu'un même utilisateur
 * compterait plusieurs fois. Ils sont donc affichés pour ce qu'ils sont : deux
 * fenêtres glissantes à l'instant T, posées en repères sur la courbe des DAU.
 *
 * Passe bento (2026-08-24) : la carte « Fidélité » met en avant UNE tuile — le
 * retour du lendemain. C'est ce chiffre qui distingue une application installée
 * d'une application réellement utilisée ; les trois autres l'éclairent.
 */

import { api } from '../lib/api';
import { rangeKey } from '../lib/range';
import type { Range } from '../lib/range';
import { useApi } from '../lib/useApi';
import { formatDateTime, formatDay, formatInt, formatPercent } from '../lib/format';
import { Async, Async2, Banner, Card, Kpi } from '../components/ui';
import { AreaStack, BarChart, Donut, LineChart } from '../charts';
import type { Categorical, Series } from '../charts';
import { seriesColor } from '../lib/lines';

/** Libellés français des plateformes et des langues, pour ne pas afficher des codes bruts. */
const PRETTY: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
  fr: 'Français',
  en: 'Anglais',
};

function toCategorical(rows: { key: string; label: string; value: number }[]): Categorical[] {
  return rows.map((r, i) => ({
    key: r.key,
    label: PRETTY[r.key] ?? r.label,
    value: r.value,
    color: seriesColor(i),
  }));
}

export function UsersPage({ range }: { range: Range }): React.ReactElement {
  const key = rangeKey(range);
  const params = { from: range.from, to: range.to };

  const overview = useApi((s) => api.overview(params, s), key);
  const active = useApi((s) => api.timeseries('active_users', params, range.bucket, s), `active|${key}`);
  const fresh = useApi((s) => api.timeseries('new_users', params, range.bucket, s), `new|${key}`);
  const platform = useApi((s) => api.breakdown('platform', params, s), `platform|${key}`);
  const version = useApi((s) => api.breakdown('app_version', params, s), `version|${key}`);
  const locale = useApi((s) => api.breakdown('locale', params, s), `locale|${key}`);

  const formatX = range.bucket === 'hour' ? formatDateTime : formatDay;

  // « Revenants » n'est pas une métrique du contrat : c'est une soustraction.
  // Elle n'a de sens que si les deux séries partagent le même seau, ce que
  // garantit l'appel avec le même `bucket`. On borne à 0 : un décalage
  // d'arrondi entre deux agrégats ne doit pas produire une aire négative.
  const stack: Series[] = (() => {
    if (!active.data || !fresh.data) return [];
    const newByT = new Map(fresh.data.points.map((p) => [p.t, p.v]));
    return [
      {
        key: 'returning',
        label: 'Revenants',
        color: 'var(--c-blue)',
        points: active.data.points.map((p) => ({ t: p.t, v: Math.max(0, p.v - (newByT.get(p.t) ?? 0)) })),
      },
      { key: 'new', label: 'Nouveaux', color: 'var(--c-gold)', points: fresh.data.points },
    ];
  })();

  return (
    <div className="stack">
      <Card
        title="Utilisateurs actifs par jour"
        hint="Combien d'athlètes ont utilisé l'application chaque jour. Les deux repères : actifs sur les 7 et sur les 30 derniers jours."
      >
        <Async state={active}>
          {(data) => (
            <LineChart
              title="Utilisateurs actifs"
              formatX={formatX}
              height={270}
              series={[{ key: 'dau', label: 'Actifs par seau', color: 'var(--c-blue)', points: data.points }]}
              refLines={
                overview.data
                  ? [
                      { label: 'WAU', value: overview.data.users.active7d, color: 'var(--c-green)' },
                      { label: 'MAU', value: overview.data.users.active30d, color: 'var(--c-city)' },
                    ]
                  : []
              }
            />
          )}
        </Async>
      </Card>

      <div className="grid grid-2">
        <Card
          title="Nouveaux et habitués"
          hint="Chaque jour : ceux qui ouvrent l'application pour la première fois, et ceux qui reviennent."
        >
          <Async2 a={active} b={fresh}>
            {() => <AreaStack title="Nouveaux et habitués" series={stack} formatX={formatX} height={250} />}
          </Async2>
        </Card>

        <Card
          title="Fidélité"
          hint="Part des athlètes qui reviennent après leur toute première visite. C’est ce qui distingue une application installée d’une application réellement utilisée."
        >
          <Async state={overview}>
            {(data) => (
              <div className="grid grid-kpi grid-kpi-2">
                <Kpi
                  featured
                  label="Reviennent le lendemain"
                  value={formatPercent(data.retention.d1, 1)}
                  hint="Revenus le lendemain de leur première utilisation."
                />
                <Kpi
                  label="Reviennent une semaine après"
                  value={formatPercent(data.retention.d7, 1)}
                  hint="Revenus une semaine après leur première utilisation."
                />
                <Kpi
                  label="Utilisateurs cumulés"
                  value={formatInt(data.users.total)}
                  hint="Athlètes différents qui ont ouvert l'application au moins une fois."
                />
                <Kpi
                  label="Nouveaux sur la période"
                  value={formatInt(data.users.new)}
                  hint="Première apparition dans la période sélectionnée."
                />
              </div>
            )}
          </Async>
        </Card>
      </div>

      {/* Bloc 2 UI/UX (2026-10-14) : plateforme, langue et version disaient la même
          chose (« avec quoi ils nous lisent ») sur trois cartes de même poids. Une
          seule carte, trois colonnes. */}
      <Card title="Leurs téléphones" hint="Plateforme, langue de l'interface et version installée, relevées au dernier passage de chaque athlète.">
        <div className="grid grid-3">
          <div>
            <p className="sub-head">Plateforme</p>
            <Async state={platform}>{(d) => <Donut title="Plateforme" rows={toCategorical(d.rows)} />}</Async>
          </div>
          <div>
            <p className="sub-head">Langue</p>
            <Async state={locale}>{(d) => <Donut title="Langue" rows={toCategorical(d.rows)} />}</Async>
          </div>
          <div>
            <p className="sub-head">Version de l'application</p>
            <Async state={version}>
              {(d) => <BarChart title="Version de l'application" rows={toCategorical(d.rows)} labelWidth={120} showShare />}
            </Async>
            <p className="small muted" style={{ margin: '8px 0 0' }}>Une vieille version qui s'attarde, ce sont des athlètes qui n'ont pas mis à jour.</p>
          </div>
        </div>
      </Card>

      <Banner tone="info" title="Rappel de confidentialité">
        Aucune de ces répartitions n'est segmentable par délégation, nom ou discipline : l'accréditation
        n'existe côté serveur que sous forme de hachage salé et n'est jamais décomposée.
      </Banner>
    </div>
  );
}
